import { z } from "zod";
import { userIdSchema } from "./identity.js";
export const signalKindSchema = z.enum([
    // Wearable + device kinds. wearable.* is the established namespace; new
    // domain-specific kinds (BP, weight, glucose) follow the same prefix so
    // the heartbeat pipeline can route by namespace prefix in future.
    "wearable.sleep",
    // A daytime nap, kept distinct from wearable.sleep so night-sleep logic
    // ("last night", sleep baseline, adherence) never mistakes a nap for a night.
    "wearable.nap",
    "wearable.hrv",
    "wearable.rhr",
    // Device-computed, motion-aware stress level (Terra daily.stress_data).
    "wearable.stress",
    // The cumulative day summary (steps, distance, calories for the local day).
    // Terra `daily` events land here; the payload's steps field is a running
    // day total, replaced in place as the provider re-syncs through the day.
    "wearable.activity",
    // A single workout/session (Terra `activity` events). Kept distinct from
    // wearable.activity so a session's partial steps can never be mistaken
    // for, or evict, the day total - same precedent as wearable.nap vs sleep.
    "wearable.workout",
    "wearable.bp.systolic",
    "wearable.bp.diastolic",
    "wearable.weight",
    "wearable.glucose",
    // Blood oxygen saturation (SpO2), Terra oxygen_data. Overnight desaturation
    // is an OSA / recovery signal.
    "wearable.spo2",
    // Respiration rate (breaths per minute), Terra oxygen_data / sleep summary.
    "wearable.respiration",
    // Peripheral skin temperature deviation from baseline, Terra temperature_data.
    "wearable.skin_temp",
    // Cardiorespiratory fitness estimate (VO2max), Terra daily / body.
    "wearable.vo2max",
    // Medication adherence + dose log. medication.dose is reserved for
    // ingested pharmacy/claims fill data (a future Express Scripts feed) and
    // is the payer-grade system of record for true PDC. It is distinct from
    // self_report.glp1_dose (see below and ADR-0016).
    "medication.dose",
    // Self-report kinds. User-entered data written with source "self-report".
    // These are the only signals a user may edit or delete (in place, by row
    // id, scoped to the author). All other kinds are immutable to the user.
    // self_report.glp1_dose is a logged injection ("I took my shot"); until a
    // claims feed exists it feeds the GLP-1 outcome bar as an honestly-labeled
    // self-reported adherence proxy, never as claims PDC. See ADR-0016.
    "self_report.weight",
    "self_report.glp1_dose",
    // A quick subjective pulse (mood / energy / stress on small scales, plus an
    // optional note), logged from the Slack check-in modal or future in-app
    // surfaces. Same editability class as the other self_report kinds.
    "self_report.checkin",
    // A member's acknowledgement that a reading dipped for a reason outside their
    // control (heat, illness, a sick child, travel). payload.reason is the
    // structured cause; payload.affectedFamilies is the behaviour families it
    // quiets; payload.effectiveThrough bounds the window (null = ongoing, hard-
    // capped). Read by the Heartbeat ConfounderLens to deny those families and
    // soften tone; it never alters baselines or trends. Same editability class as
    // the other self_report kinds, so clearing one is a delete. See confounders.ts.
    "self_report.confounder",
    // A member's social connection log entry (in-person visit, call, video
    // chat, or message). Tracked for social engagement and wellbeing support.
    "connection.log",
    // Nutrition + intake. nutrition.meal is "user logged a meal"; sodium
    // is the cardio-relevant micronutrient surfaced on the BP env card.
    "nutrition.meal",
    "nutrition.sodium_mg",
    "hydration.ml",
    // Calendar + interaction signals (existing).
    "calendar.event",
    "user.app_open",
    /*
     * A span the member spent at a desk, from whichever producer saw it: the
     * browser extension's idle state, Slack presence, or Graph presence. One
     * kind rather than three, because the rules ask "how long have they been
     * sitting", never "which client noticed". The producer is in `source`.
     */
    "user.desk_session",
    // The moment a surface judged the member to be in when they completed a
    // step. Optional and additive: absent for every surface that does not
    // measure it, and nothing reads it back into a decision. It exists so the
    // browser extension's timing gate can be evaluated rather than assumed.
    "user.completion_moment",
    "user.notification_dismissed",
    "user.microstep_added",
    "user.microstep_adjusted",
    "user.microstep_completed",
    // A reset the member actually watched, with how far they got. Distinct from
    // opening one: the card records an open the moment it is tapped, and a tap is
    // not a wind-down. Carries `completed` so the two are never conflated again.
    "user.reset_watched",
    "user.microstep_feedback",
    // The member's answer to a push that asked one: wrong step, wrong time, keep
    // it, retire it. Distinguishing a bad library entry from a badly timed one is
    // what a completion count cannot do.
    "user.microstep_verdict",
    // Contextual feedback the user gives on a specific thing in the app (a
    // section's data, a card, the app in general). payload.about names the
    // subject; payload.text is their words; payload.sentiment is up/down/neutral.
    // Read by agent context so guidance can account for what the user disputes.
    "user.feedback",
    // Adherence-loop signals emitted by the slippage detector. The
    // engine consumes them as an input to "should I re-engage this
    // member?" - the same way it consumes any wearable signal.
    "adherence.slippage_detected",
    "adherence.recovered",
    // Environmental signals (read by the Heartbeat WeatherLens and the
    // evidence renderers). A worker writes these per-user-day from the
    // existing `/api/weather` module + Cloudflare CF-IPCity / IP
    // geolocation; the lens consumes the most recent signal within a
    // small staleness window.
    "environment.weather_today",
    // Ninety days of reanalysis weather in one row, backfilled once per member so
    // a pairing can read weather against a night older than the forecast reaches.
    // Its own kind, so the readers that want today's conditions never see it.
    "environment.weather_archive",
    "environment.location_bucket",
    // Ambient contextual-grounding signals (Ambee vendor + in-house solar
    // compute). Air quality index plus dominant pollutant.
    "environment.air_quality",
    // Pollen burden (tree/grass/weed indices) for allergy-aware timing.
    "environment.pollen",
    // UV index for sun-exposure guidance.
    "environment.uv",
    // Computed solar day: sunrise, sunset, day length, daylight remaining.
    "environment.solar",
    // The wellbeing check-in: the five Thrive behaviour statements, answered
    // together once a fortnight. payload.answers carries the per-behaviour 1-5
    // agreement, payload.mean the average; payload.baseline marks the reading
    // taken at onboarding. A behaviour self-rating, not a clinical instrument:
    // it carries no screening threshold and nothing escalates on it.
    "survey.pulse",
    // Lab values not derivable from wearables. payload.value is the lab's
    // native unit (HbA1c % for "lab.hba1c"). Cadence is typically every
    // 90 days; the metabolic-reversal persona reads the most-recent value.
    "lab.hba1c",
    // External context: company news cycles (acquisitions, layoffs,
    // regulatory events). Curated feed, not public sentiment scraping.
    // SituationLens reads this to soften tone for affected users.
    "external.company_event",
]);
/**
 * The smallest change worth speaking about, in the metric's own units.
 *
 * A percentage cannot express this. 0.5% of a resting heart rate is a third of
 * a beat, and an individual's resting heart rate moves by about 3 bpm within an
 * ordinary week (Quer et al. 2020, 92,457 wearers: median within-week range 3
 * bpm, under 10 bpm for ~80% of people). Without a floor the composer picks a
 * side for a third of a beat and writes a card explaining it, which is a
 * confident sentence about noise.
 *
 * Only metrics with a measured floor belong here. Everything else keeps the
 * relative tolerance, which is what a low-variance metric like skin temperature
 * needs.

 */
const METRIC_NOISE_FLOOR = {
    "wearable.rhr": 3,
    // A bounded percentage near its ceiling, so a relative band is meaningless
    // there: 5% of 95 is 4.8 points, a desaturation no consumer device would
    // report calmly. Consumer pulse oximeters are specified to about +-2 points
    // (ISO 80601-2-61 requires an Arms of 4% or better), so anything smaller is
    // the device talking.
    "wearable.spo2": 2,
};
/**
 * How far outside a member's own variation a reading has to sit before it is
 * worth speaking about. Two of these covers roughly one reading in twenty on a
 * normal spread, the conventional line for "unusual".
 */
const SPREAD_MULTIPLE = 2;
/** Fallback band when the window is too thin to describe the member's spread. */
export const USUAL_BAND = 0.05;
/**
 * The amount a reading has to move off the member's usual before it is worth
 * speaking about: the larger of what their own readings say is ordinary and
 * what the metric says is meaningful at all.
 *
 * Both halves are required. Without the spread a single band sits at 0.1
 * standard deviations for heart rate variability and 4.5 for blood oxygen, so
 * it calls almost every HRV reading a change and almost no SpO2 one. Without
 * the floor beneath it a metric a member barely moves gets a threshold under
 * its own device noise: smart-scale weights vary about 0.1 kg day to day, which
 * would put the bar at 0.3 kg against 1-2 kg of ordinary intraday fluid swing.
 */
function usualThreshold(kind, average, opts) {
    const floor = METRIC_NOISE_FLOOR[kind] ?? (opts.band ?? USUAL_BAND) * Math.abs(average);
    const spread = opts.spread;
    const fromSpread = spread != null && Number.isFinite(spread) && spread > 0 ? SPREAD_MULTIPLE * spread : 0;
    return Math.max(fromSpread, floor);
}
/** Whether a reading has moved off the member's usual by enough to speak about. */
export function movedFromUsual(kind, latest, average, opts = {}) {
    if (average === 0)
        return false;
    return Math.abs(latest - average) >= usualThreshold(kind, average, opts);
}
/**
 * How far a reading sits past the point it became worth speaking about, signed.
 *
 * Multiples of that reading's own threshold, not spreads or percent: those are
 * different units, and anything sorting a mixed list compares them directly. A
 * blood-oxygen drop admitted by its floor with too thin a window to have a
 * spread scores a few percent, and every spread-scored row outranks it, so the
 * truncation drops exactly the pairing the threshold work exists to surface.
 * Scored this way the gate and the ranking are one quantity: this is at least
 * one whenever movedFromUsual is true, for every metric.
 */
export function deviationFromUsual(kind, latest, average, opts = {}) {
    if (average === 0)
        return 0;
    const threshold = usualThreshold(kind, average, opts);
    return threshold === 0 ? 0 : (latest - average) / threshold;
}
/**
 * Per-metric polarity: which direction vs the user's usual is GOOD news.
 * "above" = higher is better (recovery, sleep, steps, wellbeing); "below" =
 * lower is better (resting heart rate, blood pressure, stress). Metrics absent
 * here have no clear polarity (weight, glucose depend on the individual target)
 * and read as neutral. The single source of truth for metric valence across the
 * system: the microstep favourable-read gate and steer, the composed
 * rationale's warmth, the situation block handed to the generator, and the
 * well-state lens all read it here. Metrics where neither side is clearly good
 * (skin temperature, respiration, weight) are deliberately absent, and absence
 * means neutral, so a metric added without thought is never congratulated.
 */
export const METRIC_GOOD_DIRECTION = {
    "wearable.hrv": "above",
    "wearable.sleep": "above",
    "wearable.activity": "above",
    "wearable.vo2max": "above",
    "survey.pulse": "above",
    "wearable.rhr": "below",
    "wearable.bp.systolic": "below",
    "wearable.bp.diastolic": "below",
    "wearable.stress": "below",
    "wearable.spo2": "above",
    "hydration.ml": "above",
    "wearable.glucose": "below",
    "nutrition.sodium_mg": "below",
    "lab.hba1c": "below",
};
/**
 * Metric kinds whose label is grammatically plural ("daily steps"), so a
 * sentence built around the label takes "are" rather than "is". Keyed by kind
 * alongside the polarity map: absence means singular, which is every other
 * metric today.
 */
const PLURAL_METRIC_LABELS = new Set(["wearable.activity"]);
/** "is" or "are", agreeing with the metric label's grammatical number. */
export function metricLabelVerb(kind) {
    return PLURAL_METRIC_LABELS.has(kind) ? "are" : "is";
}
/** Past tense of `metricLabelVerb`, for a reading that covers a finished day. */
export function metricLabelPastVerb(kind) {
    return PLURAL_METRIC_LABELS.has(kind) ? "were" : "was";
}
/**
 * Metric kinds that ACCUMULATE through the local day (a running day total:
 * steps, hydration) rather than sampling a moment (heart rate variability, a
 * finished night's sleep hours).
 *
 * The distinction is used in two places and belongs to neither of them,
 * so it lives beside the other per-kind facts. Trend and streak logic must read
 * complete local days for these, because comparing 7am's partial total to a
 * whole-day usual invents a low day out of an early one. And copy must say
 * WHICH day it is describing, because the most recent finished day for an
 * accumulating metric is yesterday by construction: rendering it in the present
 * tense is how a member came to read yesterday's step total as today's.
 *
 * Steps alone today. Hydration also accumulates in reality, but its baseline is
 * still built per reading rather than per day, so its `latest` really is the
 * latest reading; adding it here before that changes would relabel a fresh
 * reading as a finished day.
 */
export const ACCUMULATING_METRIC_KINDS = new Set(["wearable.activity"]);
/** True when `kind` is a running day total rather than a point-in-time sample. */
export function isAccumulatingMetric(kind) {
    return ACCUMULATING_METRIC_KINDS.has(kind);
}
// A short machine label (terra:oura, google_calendar); checked where a signal is parsed.
export const signalSourceSchema = z.string().regex(/^[a-z0-9][a-z0-9_:.-]{0,63}$/);
export const signalSchema = z.object({
    userId: userIdSchema,
    kind: signalKindSchema,
    source: signalSourceSchema,
    payload: z.record(z.string(), z.unknown()).default({}),
    occurredAt: z.coerce.date(),
});
//# sourceMappingURL=signals.js.map